import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useUpdateColumn } from "@/hooks/useUpdateColumn";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  columnId: UUID;
  initialName: string;
}

export function RenameColumnDialog({ open, onOpenChange, boardId, columnId, initialName }: Props) {
  const nameId = useId();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateColumn();

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== initialName && !mutation.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await mutation.mutateAsync({ boardId, columnId, payload: { name: trimmed } });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.unknownError);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.renameColumnTitle}
      description={t.renameColumnDescription}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.columnName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
            {mutation.isPending ? t.saving : t.save}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
